import { MutableRefObject, useEffect } from 'react';

/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import { useSelect } from 'downshift';

import { BaseCheckIcon, UpArrowIcon, DownArrowIcon } from '@assets/icons';
import { PaletteColor, SizeUnit } from '@utils/types';
import { useSizes, ResponsiveSizes } from '@components/utils/hooks/useSize';

import {
  Container,
  LabelContainer,
  Options,
  Option,
  OptionsContainer,
  Separator,
  CustomIcon,
} from './styled';
import { ItemType, useHighLightDropDownItem } from './hooks';

const defaultGetKey = <T,>(x: T) => (x ? String(x) : '');

const defaultLabelRenderer = <T,>(x: T) => <label>{defaultGetKey(x)}</label>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DefaultRendererType<T = any> = (option: T) => React.ReactNode;

export type DropdownSelectProps<T = unknown> = {
  items?: T[];
  getKey?: (item: T | null) => string;
  renderLabel?: (
    item: T,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    defaultRender: DefaultRendererType<T>
  ) => React.ReactNode;
  renderOption?: (
    item: T,
    defaultRender: DefaultRendererType
  ) => React.ReactNode;
  defaultSelectedItem?: T;
  height?: SizeUnit;
  paddings?: ResponsiveSizes;
  showOptionsWhenNumberItemsExceed?: number;
  onChange?: (item: T) => void;
  hasErrors?: boolean;
  hasError?: boolean;
  disabled?: boolean;
  optionsZIndex?: number;
  resetRef?: MutableRefObject<(() => void) | undefined>;
  containerBackground?: PaletteColor;
  hideIcon?: boolean;
  phoneError?: boolean;
};

const useResponsivePaddings = (paddings: ResponsiveSizes) =>
  `${useSizes(paddings)}px`;

const enableDropdownOpening = <T,>(
  items: T[],
  condition: boolean,
  showOptionsWhenNumberItemsExceed: number
) => items.length > showOptionsWhenNumberItemsExceed && condition;

export function Dropdown<T = unknown>({
  items = [],
  getKey = defaultGetKey,
  renderLabel = defaultLabelRenderer,
  renderOption = defaultLabelRenderer,
  defaultSelectedItem,
  height,
  paddings = { large: 15, small: 15 },
  showOptionsWhenNumberItemsExceed = 0,
  onChange,
  optionsZIndex,
  resetRef,
  containerBackground,
  hideIcon = false,
  disabled,
  phoneError,
  hasErrors,
  ...rest
}: DropdownSelectProps<T>) {
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    reset,
    setHighlightedIndex,
  } = useSelect({
    items,
    itemToString: getKey,
    defaultSelectedItem,
    onSelectedItemChange: ({ selectedItem }) => onChange?.(selectedItem!),
  });

  useEffect(() => {
    if (resetRef) {
      resetRef.current = reset;
    }
  }, [reset, resetRef]);

  const shouldOpen = enableDropdownOpening(
    items,
    isOpen && !disabled,
    showOptionsWhenNumberItemsExceed
  );

  useHighLightDropDownItem({
    items: [...items] as ItemType[],
    setHighlightedIndex,
    isOpen,
  });

  const Arrow = shouldOpen ? UpArrowIcon : DownArrowIcon;
  const horizontalPaddings = useResponsivePaddings(paddings);

  return (
    <Container
      hasError={hasErrors || phoneError}
      isOpen={shouldOpen}
      displayPlaceholder={!selectedItem}
      height={height}
      disabled={disabled}
      horizontalPaddings={horizontalPaddings}
      containerBackground={containerBackground}
      {...rest}
    >
      <LabelContainer {...getToggleButtonProps()}>
        {renderLabel(selectedItem!, defaultLabelRenderer)}
        {!hideIcon && (
          <CustomIcon
            icon={Arrow}
            fontSize={16}
            paletteColor={rest.hasError ? 'tomato' : undefined}
          />
        )}
      </LabelContainer>

      <>
        <Separator visible={shouldOpen} />

        <OptionsContainer
          optionsZIndex={optionsZIndex}
          isOpen={shouldOpen}
          {...rest}
        >
          <div {...getMenuProps()}>
            {shouldOpen && (
              <Options>
                {items?.map((item, index) => (
                  <Option
                    isActive={
                      defaultSelectedItem && item === defaultSelectedItem
                    }
                    isHighlighted={highlightedIndex === index}
                    key={`${getKey(item)}${index}`}
                    {...getItemProps({ item, index })}
                  >
                    <span>{renderOption(item, defaultLabelRenderer)}</span>
                    {defaultSelectedItem && item === defaultSelectedItem && (
                      <CustomIcon icon={BaseCheckIcon} fontSize={20} />
                    )}
                  </Option>
                ))}
              </Options>
            )}
            <div tabIndex={0} />
          </div>
        </OptionsContainer>
      </>
    </Container>
  );
}

export default Dropdown;
